import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { ErrorState } from "./error-state";
import { Input } from "./input";

export interface ConfirmDialogProps {
  title: ReactNode;
  description: ReactNode;
  /** A value to type before confirming (Studio spec §4), or none. */
  typedValue?: string | null;
  confirmLabel: string;
  destructive?: boolean;
  pending?: boolean;
  /** Whether the fields of `children` allow confirming. */
  ready?: boolean;
  /** The failure of the last attempt, shown in the dialog. */
  error?: unknown;
  /** Fields shown above the typed value. */
  children?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}

/** A yes-or-no question about one action, open while rendered. */
export function ConfirmDialog({
  title,
  description,
  typedValue = null,
  confirmLabel,
  destructive = false,
  pending = false,
  ready = true,
  error,
  children,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const inputId = useId();
  const named = typedValue === null || typed.trim() === typedValue;
  const body = children || typedValue !== null || error;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (named && ready && !pending) onConfirm();
          }}
        >
          <DialogHeader>
            <DialogTitle className="break-all">{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {body ? (
            <DialogBody className="grid gap-3">
              {children}
              {typedValue !== null && (
                <div>
                  <label htmlFor={inputId} className="mb-1.5 block text-sm font-semibold text-ink">
                    {t("ui.command.typeName", { name: typedValue })}
                  </label>
                  <Input
                    id={inputId}
                    value={typed}
                    autoComplete="off"
                    onChange={(event) => setTyped(event.target.value)}
                  />
                </div>
              )}
              {error ? <ErrorState error={error} /> : null}
            </DialogBody>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("ui.command.cancel")}
            </Button>
            <Button
              type="submit"
              variant={destructive ? "destructive" : "default"}
              disabled={!named || !ready || pending}
            >
              {pending ? t("ui.command.running") : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
